import { abilityNumber, type Rules } from "odyssey-shared";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";
import { itemGlyph } from "../bag/describe.ts";
import type { BagSnapshot } from "../bag/store.ts";

/**
 * The default bar's item slot (P2.8-T12, ADR-091 decision 14, the author's ruling 4): while the player has not arranged
 * the bar (ADR-121 decision 2.3), its last slot shows the first consumable in the bag in slot order, with the total
 * carried of that item. `resolve.ts` draws it and `arranged.ts` writes it down at the player's first change.
 */

/**
 * What the item slot holds: the item's rules id, name, glyph (null for none the page draws) and icon key (null for
 * none, ADR-111 decision 1), the total carried
 * across every bag slot holding it, the first bag slot holding it, and the number of the ability record its use runs.
 */
export interface BarItem {
  readonly item: string;
  readonly name: string;
  readonly glyph: GlyphName | null;
  readonly icon: string | null;
  readonly count: number;
  readonly slot: number;
  readonly record: number;
}

/**
 * The item the bar's item slot shows: the first consumable in the bag in slot order whose use names a record the
 * rules hold, with every unit of it the bag carries.
 *
 * @param bag The page's bag.
 * @param rules The page's rules.
 * @returns The item, or null when the bag holds no consumable.
 * @example
 * barItemOf({ open: false, size: 20, slots: [{ item: "minor_healing_potion", quantity: 3, pending: false }] }, rules)?.count;
 * // 3
 */
export function barItemOf(bag: BagSnapshot, rules: Rules): BarItem | null {
  let found: { readonly item: string; readonly name: string; readonly slot: number; readonly record: number } | null = null;
  let count = 0;
  for (let slot = 0; slot < bag.slots.length; slot += 1) {
    const held = bag.slots[slot];
    if (held === undefined || held.item === null) {
      continue;
    }
    if (found === null) {
      const item = rules.items.get(held.item);
      if (item === undefined || item.kind !== "consumable" || item.use === null || !rules.abilities.has(item.use)) {
        continue;
      }
      found = { item: held.item, name: item.name, slot, record: abilityNumber(rules, item.use) };
    }
    if (held.item === found.item) {
      count += held.quantity;
    }
  }
  return found === null ? null : { ...found, glyph: itemGlyph(rules, found.item), icon: rules.items.get(found.item)?.icon ?? null, count };
}
